import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { APIProvider } from '@vis.gl/react-google-maps';
import {
  Search,
  MapPin,
  Navigation,
  Star,
  Loader2,
  RotateCcw,
  X,
} from 'lucide-react';
import { useApp } from '../../context/AppContext';
import { SERVICE_CATEGORIES } from '../../data/categories';
import { VerificationBadge } from '../../components/common/VerificationBadge';
import { CategoryIcon } from '../../components/common/CategoryIcon';
import { Profile } from '../../types';
import {
  PlacesAutocompleteInputInner,
  CustomerSelectedLocation,
} from '../../components/location/CustomerNearbyLocationBar';
import {
  ALLOWED_NEARBY_RADII_KM,
  DEFAULT_NEARBY_RADIUS_KM,
  NearbyRadiusKm,
  getGoogleMapsBrowserApiKey,
} from '../../config/googleMaps';
import {
  calculateHaversineDistanceKm,
  computeBoundingBox,
  isValidCoordinatePair,
  isWithinNigeriaBounds,
  resolveFilterMapCenter,
  resolveValidatedProfessionalCoordinates,
} from '../../utils/locationCoordinates';

const POPULAR_CATEGORY_SHORTCUTS: Array<{ slug: string; label: string }> = [
  { slug: '', label: 'All Services' },
  { slug: 'plumber', label: 'Plumbing' },
  { slug: 'electrician', label: 'Electrical Repairs' },
  { slug: 'generator-technician', label: 'Generator Repairs' },
  { slug: 'ac-technician', label: 'Air Conditioning' },
  { slug: 'cleaner', label: 'Cleaning' },
  { slug: 'solar-technician', label: 'Solar & Inverter' },
  { slug: 'carpenter', label: 'Carpentry' },
  { slug: 'painter', label: 'Painting' },
  { slug: 'auto-mechanic', label: 'Auto Mechanic' },
];

export const FindProfessionalPage: React.FC = () => {
  const { profiles, navigateTo, viewParams } = useApp();

  // Search & Filter States
  const [searchInput, setSearchInput] = useState(viewParams?.searchQuery || '');
  const [appliedSearchQuery, setAppliedSearchQuery] = useState(viewParams?.searchQuery || '');
  const [selectedCategory, setSelectedCategory] = useState(viewParams?.selectedCategory || '');
  const [selectedSubcategory, setSelectedSubcategory] = useState(viewParams?.selectedSubcategory || '');
  const [selectedState, setSelectedState] = useState(viewParams?.selectedState || '');
  const [selectedLga, setSelectedLga] = useState(viewParams?.selectedLga || '');
  const [locationText, setLocationText] = useState(
    viewParams?.selectedLga && viewParams?.selectedState
      ? `${viewParams.selectedLga}, ${viewParams.selectedState}`
      : viewParams?.selectedState || ''
  );

  // Google Maps / GPS Location & Radius State
  const [customerLocation, setCustomerLocation] = useState<CustomerSelectedLocation | null>(null);
  const [radiusKm, setRadiusKm] = useState<NearbyRadiusKm>(DEFAULT_NEARBY_RADIUS_KM);
  const [isLocatingGps, setIsLocatingGps] = useState(false);
  const [gpsError, setGpsError] = useState<string | null>(null);

  // Server Discovery & Loading State
  const [serverPros, setServerPros] = useState<Profile[]>([]);
  const [serverNearbyPros, setServerNearbyPros] = useState<Profile[] | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [fetchError, setFetchError] = useState<string | null>(null);
  const [failedImageIds, setFailedImageIds] = useState<Record<string, boolean>>({});

  // Sync incoming viewParams when navigating from category links or header
  useEffect(() => {
    if (viewParams?.selectedCategory !== undefined) {
      setSelectedCategory(viewParams.selectedCategory);
      setSelectedSubcategory('');
    }
    if (viewParams?.selectedState !== undefined) {
      setSelectedState(viewParams.selectedState);
    }
    if (viewParams?.selectedLga !== undefined) {
      setSelectedLga(viewParams.selectedLga);
    }
    if (viewParams?.searchQuery !== undefined) {
      setSearchInput(viewParams.searchQuery);
      setAppliedSearchQuery(viewParams.searchQuery);
    }
  }, [viewParams]);

  // Fetch verified/active professionals from backend discovery endpoint
  const fetchDirectoryProfessionals = useCallback(async () => {
    setIsLoading(true);
    setFetchError(null);
    try {
      const params = new URLSearchParams({ limit: '100' });
      if (selectedCategory) params.set('category', selectedCategory);
      if (selectedSubcategory) params.set('subcategory', selectedSubcategory);
      if (selectedState) params.set('state', selectedState);
      if (selectedLga) params.set('lga', selectedLga);
      if (appliedSearchQuery.trim()) params.set('q', appliedSearchQuery.trim());

      const res = await fetch(`/api/professionals/search?${params.toString()}`);
      if (!res.ok) {
        throw new Error('Could not load professionals at this moment.');
      }
      const data = await res.json();
      if (data?.success && Array.isArray(data.professionals)) {
        setServerPros(data.professionals as Profile[]);
      }
    } catch {
      // Keep existing profiles from context if network request fails
      if (profiles.filter((p) => p.role === 'professional').length === 0) {
        setFetchError('Unable to load professionals right now. Please check your connection and try again.');
      }
    } finally {
      setIsLoading(false);
    }
  }, [selectedCategory, selectedSubcategory, selectedState, selectedLga, appliedSearchQuery, profiles]);

  useEffect(() => {
    fetchDirectoryProfessionals();
  }, [fetchDirectoryProfessionals]);

  // Query backend /api/professionals/nearby whenever customerLocation or filters change
  useEffect(() => {
    if (!customerLocation || !isValidCoordinatePair(customerLocation.lat, customerLocation.lng)) {
      setServerNearbyPros(null);
      return;
    }

    let cancelled = false;
    const params = new URLSearchParams({
      lat: String(customerLocation.lat),
      lng: String(customerLocation.lng),
      radiusKm: String(radiusKm),
      onlyAvailable: 'false',
      onlyVerified: 'false',
      sort: 'nearest',
    });
    if (selectedCategory) params.set('category', selectedCategory);
    if (selectedSubcategory) params.set('subcategory', selectedSubcategory);
    if (selectedState) params.set('state', selectedState);
    if (selectedLga) params.set('lga', selectedLga);
    if (appliedSearchQuery.trim()) params.set('q', appliedSearchQuery.trim());

    fetch(`/api/professionals/nearby?${params.toString()}`)
      .then((res) => res.json())
      .then((data) => {
        if (cancelled) return;
        if (data?.success && Array.isArray(data.professionals)) {
          setServerNearbyPros(data.professionals as Profile[]);
        }
      })
      .catch(() => {
        // Deterministic client-side Haversine calculation below serves as immediate fallback
      });

    return () => {
      cancelled = true;
    };
  }, [
    customerLocation?.lat,
    customerLocation?.lng,
    radiusKm,
    selectedCategory,
    selectedSubcategory,
    selectedState,
    selectedLga,
    appliedSearchQuery,
  ]);

  // Merge server professionals and context professionals by unique ID (never duplicating or using fake fallbacks)
  const allProfessionals = useMemo(() => {
    const map = new Map<string, Profile>();
    for (const p of profiles) {
      if (p && p.id && p.role === 'professional' && p.accountStatus !== 'suspended' && !p.isSuspended) {
        map.set(p.id, p);
      }
    }
    for (const p of serverPros) {
      if (p && p.id && p.role === 'professional' && p.accountStatus !== 'suspended' && !p.isSuspended) {
        const existing = map.get(p.id);
        map.set(p.id, existing ? { ...existing, ...p } : p);
      }
    }
    return Array.from(map.values());
  }, [profiles, serverPros]);

  // Synchronized filtering + location/radius matching
  const filteredPros = useMemo(() => {
    const bbox =
      customerLocation && radiusKm
        ? computeBoundingBox(customerLocation.lat, customerLocation.lng, radiusKm)
        : null;

    const results: Profile[] = [];
    const activeQuery = appliedSearchQuery.toLowerCase().trim();
    const typedLocation = (!customerLocation ? locationText : '').toLowerCase().trim();

    for (const pro of allProfessionals) {
      if (!pro.fullName || !pro.id) continue;

      // 1. Category Filter
      if (selectedCategory) {
        const targetCat = selectedCategory.toLowerCase();
        const primaryMatch = pro.primaryCategory?.toLowerCase() === targetCat;
        const secondaryMatch = (pro.secondaryCategories || []).some(
          (c) => c.toLowerCase() === targetCat
        );
        if (!primaryMatch && !secondaryMatch) continue;
      }

      // 2. Subcategory Filter
      if (selectedSubcategory) {
        const targetSub = selectedSubcategory.toLowerCase();
        const subMatch =
          (pro.specialties || []).some(
            (s) => s.toLowerCase().includes(targetSub) || targetSub.includes(s.toLowerCase())
          ) ||
          (pro.skills || []).some(
            (s) => s.toLowerCase().includes(targetSub) || targetSub.includes(s.toLowerCase())
          );
        if (!subMatch) continue;
      }

      // 3. State Filter
      if (selectedState) {
        const targetState = selectedState.toLowerCase();
        const proState = (pro.state || '').toLowerCase();
        if (
          proState !== targetState &&
          !proState.includes(targetState) &&
          !targetState.includes(proState)
        ) {
          continue;
        }
      }

      // 4. LGA / Service Area Filter
      if (selectedLga) {
        const targetLgaLower = selectedLga.toLowerCase();
        const baseMatch = pro.lga?.toLowerCase() === targetLgaLower;
        const areaMatch = (pro.serviceAreas || []).some(
          (a) => a.toLowerCase() === targetLgaLower || a.toLowerCase().includes(targetLgaLower)
        );
        const serviceLgaMatch = (pro.serviceLgas || []).some(
          (l) => l.toLowerCase() === targetLgaLower || l.toLowerCase().includes(targetLgaLower)
        );
        if (!baseMatch && !areaMatch && !serviceLgaMatch) continue;
      }

      // 5. Typed location text filter (when user types a city/state/LGA without selecting a pin)
      if (typedLocation) {
        const inState = (pro.state || '').toLowerCase().includes(typedLocation);
        const inLga = (pro.lga || '').toLowerCase().includes(typedLocation);
        const inCity = (pro.cityArea || '').toLowerCase().includes(typedLocation);
        const inAreas = (pro.serviceAreas || []).some((a) =>
          a.toLowerCase().includes(typedLocation)
        );
        if (!inState && !inLga && !inCity && !inAreas) continue;
      }

      // 6. Service / Skill / Name Search Query
      if (activeQuery) {
        const catObj = SERVICE_CATEGORIES.find(
          (c) => c.slug.toLowerCase() === pro.primaryCategory?.toLowerCase()
        );
        const inName = pro.fullName.toLowerCase().includes(activeQuery);
        const inBiz = (pro.businessName || '').toLowerCase().includes(activeQuery);
        const inBio = (pro.bio || '').toLowerCase().includes(activeQuery);
        const inSkills = (pro.skills || []).some((s) => s.toLowerCase().includes(activeQuery));
        const inSpecs = (pro.specialties || []).some((s) => s.toLowerCase().includes(activeQuery));
        const inCatSlug = (pro.primaryCategory || '').toLowerCase().includes(activeQuery);
        const inCatName = (catObj?.name || '').toLowerCase().includes(activeQuery);
        if (!inName && !inBiz && !inBio && !inSkills && !inSpecs && !inCatSlug && !inCatName) {
          continue;
        }
      }

      // 7. Geographic Coordinates & Radius Filtering (when customerLocation is set)
      if (customerLocation) {
        const coords = resolveValidatedProfessionalCoordinates(pro);
        if (!coords || !isValidCoordinatePair(coords.lat, coords.lng)) {
          continue;
        }
        if (
          bbox &&
          (coords.lat < bbox.minLat ||
            coords.lat > bbox.maxLat ||
            coords.lng < bbox.minLng ||
            coords.lng > bbox.maxLng)
        ) {
          continue;
        }
        const distKm = calculateHaversineDistanceKm(customerLocation, coords);
        if (distKm > radiusKm) {
          continue;
        }
        results.push({
          ...pro,
          serviceLatitude: coords.lat,
          serviceLongitude: coords.lng,
          distanceKm: distKm,
        });
      } else {
        results.push({
          ...pro,
          distanceKm: undefined,
        });
      }
    }

    const finalPool =
      serverNearbyPros && customerLocation && serverNearbyPros.length > 0
        ? [...serverNearbyPros]
        : results;

    // Sort verified first, then nearest if location is active, then highest rating
    finalPool.sort((a, b) => {
      if (customerLocation) {
        const dA = typeof a.distanceKm === 'number' ? a.distanceKm : Number.POSITIVE_INFINITY;
        const dB = typeof b.distanceKm === 'number' ? b.distanceKm : Number.POSITIVE_INFINITY;
        if (Math.abs(dA - dB) > 0.05) return dA - dB;
      }
      if (a.verificationStatus === 'verified' && b.verificationStatus !== 'verified') return -1;
      if (b.verificationStatus === 'verified' && a.verificationStatus !== 'verified') return 1;
      const rA = (a.reviewCount || 0) > 0 ? a.rating || 0 : 0;
      const rB = (b.reviewCount || 0) > 0 ? b.rating || 0 : 0;
      if (rB !== rA) return rB - rA;
      return (b.completedJobsCount || 0) - (a.completedJobsCount || 0);
    });

    return finalPool;
  }, [
    allProfessionals,
    serverNearbyPros,
    customerLocation,
    radiusKm,
    selectedCategory,
    selectedSubcategory,
    selectedState,
    selectedLga,
    locationText,
    appliedSearchQuery,
  ]);

  const handleSearchSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setAppliedSearchQuery(searchInput.trim());

    // If user typed a known state or LGA in locationText without picking an autocomplete suggestion, resolve coordinates or filter
    if (!customerLocation && locationText.trim()) {
      const cleaned = locationText.trim();
      const parts = cleaned.split(',').map((s) => s.trim()).filter(Boolean);
      if (parts.length >= 2) {
        const resolved = resolveFilterMapCenter(parts[1], parts[0]);
        if (resolved?.center) {
          setSelectedLga(parts[0]);
          setSelectedState(parts[1]);
        }
      }
    }
  };

  const handleUseCurrentLocation = () => {
    setGpsError(null);
    if (typeof navigator === 'undefined' || !navigator.geolocation) {
      setGpsError('Geolocation is not supported by your browser. Please type your location instead.');
      return;
    }

    setIsLocatingGps(true);
    navigator.geolocation.getCurrentPosition(
      async (position) => {
        const { latitude, longitude, accuracy } = position.coords;
        if (!isValidCoordinatePair(latitude, longitude)) {
          setIsLocatingGps(false);
          setGpsError('Could not read valid coordinates. Please enter your location manually.');
          return;
        }

        let resolvedLabel = isWithinNigeriaBounds(latitude, longitude)
          ? `Current Location (${latitude.toFixed(3)}, ${longitude.toFixed(3)})`
          : `GPS Location (${latitude.toFixed(3)}, ${longitude.toFixed(3)})`;
        let detectedState: string | undefined;
        let detectedLga: string | undefined;

        try {
          const revRes = await fetch(
            `/api/location/reverse?lat=${encodeURIComponent(latitude)}&lon=${encodeURIComponent(longitude)}`
          );
          if (revRes.ok) {
            const revData = await revRes.json();
            if (revData?.result?.formattedAddress) {
              resolvedLabel = revData.result.formattedAddress;
              detectedState = revData.result.state;
              detectedLga = revData.result.lga;
            }
          }
        } catch {
          // Keep coordinate label
        }

        setLocationText(resolvedLabel);
        setCustomerLocation({
          lat: Number(latitude.toFixed(5)),
          lng: Number(longitude.toFixed(5)),
          label: resolvedLabel,
          source: 'browser_gps',
          accuracyMeters: accuracy ? Math.round(accuracy) : undefined,
          state: detectedState,
          lga: detectedLga,
        });
        setIsLocatingGps(false);
      },
      () => {
        setIsLocatingGps(false);
        setGpsError('Location access was declined or unavailable. Please type your city or area.');
      },
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 }
    );
  };

  const clearAllFilters = () => {
    setSearchInput('');
    setAppliedSearchQuery('');
    setSelectedCategory('');
    setSelectedSubcategory('');
    setSelectedState('');
    setSelectedLga('');
    setLocationText('');
    setCustomerLocation(null);
    setGpsError(null);
    setRadiusKm(DEFAULT_NEARBY_RADIUS_KM);
  };

  const hasActiveFilters = Boolean(
    searchInput.trim() ||
      appliedSearchQuery.trim() ||
      selectedCategory ||
      selectedSubcategory ||
      selectedState ||
      selectedLga ||
      locationText.trim() ||
      customerLocation
  );

  const mapsApiKey = getGoogleMapsBrowserApiKey();

  const pageContent = (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-950 text-slate-900 dark:text-slate-100 py-10 px-4 sm:px-6 lg:px-8 transition-colors">
      <div className="max-w-7xl mx-auto space-y-8">
        {/* 1 & 2. Page Heading & Short Description */}
        <header className="space-y-2">
          <h1 className="text-3xl sm:text-4xl font-bold tracking-tight text-slate-900 dark:text-white">
            Find a Professional
          </h1>
          <p className="text-base text-slate-600 dark:text-slate-300">
            Find trusted professionals for your needs.
          </p>
        </header>

        {/* 3. Search Area */}
        <section
          aria-label="Search professionals"
          className="bg-white dark:bg-slate-900 rounded-2xl border border-slate-200 dark:border-slate-800 p-4 sm:p-5 shadow-xs space-y-3 transition-colors"
        >
          <form
            onSubmit={handleSearchSubmit}
            className="grid grid-cols-1 md:grid-cols-12 gap-3 items-stretch"
          >
            {/* Service or Skill Input */}
            <div className="md:col-span-5 relative">
              <Search className="w-4 h-4 text-slate-400 absolute left-3.5 top-1/2 -translate-y-1/2 pointer-events-none" />
              <input
                type="text"
                value={searchInput}
                onChange={(e) => {
                  setSearchInput(e.target.value);
                  setAppliedSearchQuery(e.target.value);
                }}
                placeholder="Search service, skill, or professional name..."
                aria-label="Search for a service or skill"
                className="w-full pl-10 pr-9 py-2.5 rounded-xl border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/90 focus:bg-white dark:focus:bg-slate-800 text-sm text-slate-900 dark:text-white placeholder:text-slate-400 dark:placeholder:text-slate-500 focus:outline-hidden focus:ring-2 focus:ring-blue-500/20 focus:border-blue-600 transition-colors"
              />
              {searchInput && (
                <button
                  type="button"
                  onClick={() => {
                    setSearchInput('');
                    setAppliedSearchQuery('');
                  }}
                  className="absolute right-2.5 top-1/2 -translate-y-1/2 p-1 text-slate-400 hover:text-slate-600 cursor-pointer"
                  title="Clear search"
                >
                  <X className="w-3.5 h-3.5" />
                </button>
              )}
            </div>

            {/* Location Input with Google Maps Autocomplete + GPS button */}
            <div className="md:col-span-5 flex items-stretch gap-2">
              <PlacesAutocompleteInputInner
                currentLabel={locationText}
                placeholder="Location (city, LGA, or street address)..."
                onTextChange={(txt) => {
                  setLocationText(txt);
                  if (!txt.trim()) {
                    setCustomerLocation(null);
                    setSelectedState('');
                    setSelectedLga('');
                  }
                }}
                onClearLocation={() => {
                  setLocationText('');
                  setCustomerLocation(null);
                  setSelectedState('');
                  setSelectedLga('');
                }}
                onSelectLocation={(loc) => {
                  setGpsError(null);
                  setLocationText(loc.label);
                  setCustomerLocation(loc);
                  if (loc.state) setSelectedState(loc.state);
                  if (loc.lga) setSelectedLga(loc.lga);
                }}
              />
              <button
                type="button"
                onClick={handleUseCurrentLocation}
                disabled={isLocatingGps}
                title="Use my current location"
                aria-label="Use my current location"
                className="px-3 py-2.5 rounded-xl border border-slate-200 bg-slate-50 hover:bg-slate-100 text-slate-700 text-xs font-medium flex items-center gap-1.5 shrink-0 transition-colors cursor-pointer disabled:opacity-60"
              >
                {isLocatingGps ? (
                  <Loader2 className="w-4 h-4 text-emerald-600 animate-spin" />
                ) : (
                  <Navigation className="w-4 h-4 text-emerald-600" />
                )}
                <span className="hidden xl:inline whitespace-nowrap">Near me</span>
              </button>
            </div>

            {/* Prominent Search Button */}
            <div className="md:col-span-2 flex">
              <button
                type="submit"
                className="w-full py-2.5 px-5 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-semibold shadow-xs transition-colors flex items-center justify-center gap-2 cursor-pointer whitespace-nowrap"
              >
                <Search className="w-4 h-4" />
                <span>Search</span>
              </button>
            </div>
          </form>

          {/* Optional Radius Selector when a coordinate location is active */}
          {customerLocation && (
            <div className="flex flex-wrap items-center justify-between gap-2 pt-2 border-t border-slate-100 text-xs text-slate-600">
              <div className="flex items-center gap-1.5 min-w-0">
                <MapPin className="w-3.5 h-3.5 text-emerald-600 shrink-0" />
                <span className="truncate">
                  Showing professionals within <strong>{radiusKm} km</strong> of{' '}
                  <strong className="text-slate-900">{customerLocation.label}</strong>
                </span>
              </div>
              <div className="flex items-center gap-1.5">
                <span className="text-slate-500">Radius:</span>
                {ALLOWED_NEARBY_RADII_KM.map((r) => (
                  <button
                    key={r}
                    type="button"
                    onClick={() => setRadiusKm(r)}
                    className={`px-2.5 py-1 rounded-lg text-xs font-medium transition-colors cursor-pointer ${
                      radiusKm === r
                        ? 'bg-emerald-600 text-white'
                        : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                    }`}
                  >
                    {r} km
                  </button>
                ))}
              </div>
            </div>
          )}

          {gpsError && (
            <p className="text-xs text-amber-700 pt-1" role="status">
              {gpsError}
            </p>
          )}
        </section>

        {/* 4. Category Shortcuts */}
        <section aria-label="Popular service categories" className="space-y-3">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <h2 className="text-sm font-semibold text-slate-700 dark:text-slate-200">Popular Categories</h2>
            <div className="flex items-center gap-3">
              <select
                value={selectedCategory}
                onChange={(e) => {
                  setSelectedCategory(e.target.value);
                  setSelectedSubcategory('');
                }}
                aria-label="Select any service category"
                className="px-3 py-1.5 rounded-lg border border-slate-200 bg-white text-xs font-medium text-slate-700 focus:outline-hidden focus:border-emerald-600 max-w-[200px] sm:max-w-none"
              >
                <option value="">All Supported Categories</option>
                {SERVICE_CATEGORIES.filter((c) => c.isActive !== false).map((cat) => (
                  <option key={cat.slug} value={cat.slug}>
                    {cat.name}
                  </option>
                ))}
              </select>

              {hasActiveFilters && (
                <button
                  type="button"
                  onClick={clearAllFilters}
                  className="text-xs font-medium text-slate-600 dark:text-slate-300 hover:text-slate-900 dark:hover:text-white underline underline-offset-4 cursor-pointer whitespace-nowrap"
                >
                  Clear filters
                </button>
              )}
            </div>
          </div>

          <div className="flex items-center gap-2 overflow-x-auto pb-1.5 no-scrollbar flex-nowrap sm:flex-wrap">
            {POPULAR_CATEGORY_SHORTCUTS.map((item) => {
              const isSelected = selectedCategory.toLowerCase() === item.slug.toLowerCase();
              const catObj = SERVICE_CATEGORIES.find((c) => c.slug === item.slug);
              return (
                <button
                  key={item.slug || 'all'}
                  type="button"
                  onClick={() => {
                    setSelectedCategory(isSelected && item.slug !== '' ? '' : item.slug);
                    setSelectedSubcategory('');
                  }}
                  className={`px-3.5 py-2 rounded-xl text-xs font-semibold transition-colors flex items-center gap-2 cursor-pointer whitespace-nowrap shrink-0 border ${
                    isSelected
                      ? 'bg-slate-900 text-white border-slate-900'
                      : 'bg-white text-slate-700 border-slate-200 hover:border-slate-300 hover:bg-slate-50'
                  }`}
                >
                  {catObj && (
                    <CategoryIcon
                      slug={catObj.slug}
                      className={`w-3.5 h-3.5 ${isSelected ? 'text-emerald-400' : 'text-emerald-600'}`}
                    />
                  )}
                  <span>{item.label}</span>
                </button>
              );
            })}
          </div>
        </section>

        {/* 5. Professional Results */}
        <section aria-label="Professional results" className="space-y-4">
          <div className="flex items-center justify-between text-xs text-slate-600 dark:text-slate-300">
            <span>
              {isLoading ? (
                'Loading professionals...'
              ) : (
                <>
                  Showing <strong className="text-slate-900 dark:text-white tabular-nums">{filteredPros.length}</strong>{' '}
                  {filteredPros.length === 1 ? 'professional' : 'professionals'}
                </>
              )}
            </span>
          </div>

          {isLoading && filteredPros.length === 0 ? (
            <div className="bg-white rounded-2xl border border-slate-200 p-12 text-center space-y-3">
              <Loader2 className="w-7 h-7 text-emerald-600 animate-spin mx-auto" />
              <p className="text-sm font-medium text-slate-700">Finding available professionals...</p>
            </div>
          ) : fetchError && filteredPros.length === 0 ? (
            <div className="bg-white rounded-2xl border border-slate-200 p-10 text-center space-y-4 max-w-md mx-auto">
              <p className="text-sm text-slate-700">{fetchError}</p>
              <button
                type="button"
                onClick={fetchDirectoryProfessionals}
                className="px-4 py-2 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-semibold inline-flex items-center gap-1.5 cursor-pointer"
              >
                <RotateCcw className="w-3.5 h-3.5" />
                <span>Retry</span>
              </button>
            </div>
          ) : filteredPros.length === 0 ? (
            <div className="bg-white rounded-2xl border border-slate-200 p-10 text-center space-y-4 max-w-lg mx-auto">
              <div className="w-12 h-12 rounded-full bg-slate-100 text-slate-500 flex items-center justify-center mx-auto">
                <Search className="w-5 h-5" />
              </div>
              <div className="space-y-1">
                <h3 className="text-base font-bold text-slate-900">No matching professionals found</h3>
                <p className="text-sm text-slate-600">
                  We could not find any professionals matching your current search criteria. Try clearing your filters or searching another location.
                </p>
              </div>
              <div className="flex flex-wrap items-center justify-center gap-2 pt-1">
                {customerLocation && radiusKm < 50 && (
                  <button
                    type="button"
                    onClick={() => setRadiusKm(50)}
                    className="px-4 py-2 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-semibold cursor-pointer"
                  >
                    Expand radius to 50 km
                  </button>
                )}
                <button
                  type="button"
                  onClick={clearAllFilters}
                  className="px-4 py-2 rounded-xl border border-slate-300 hover:bg-slate-50 text-slate-700 text-xs font-semibold inline-flex items-center gap-1.5 cursor-pointer"
                >
                  <RotateCcw className="w-3.5 h-3.5" />
                  <span>Reset Search</span>
                </button>
              </div>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredPros.map((pro) => {
                const categoryObj = SERVICE_CATEGORIES.find(
                  (c) => c.slug.toLowerCase() === pro.primaryCategory?.toLowerCase()
                );
                const tradeLabel =
                  categoryObj?.name ||
                  (pro.primaryCategory
                    ? pro.primaryCategory.replace(/-/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase())
                    : 'Professional Service');
                const isVerified = pro.verificationStatus === 'verified';
                const hasRealReviews = (pro.reviewCount || 0) > 0 && (pro.rating || 0) > 0;
                const photoUrl = pro.verifiedProfilePhotoUrl || pro.avatarUrl;
                const showPhoto = Boolean(photoUrl && !failedImageIds[pro.id]);
                const locationLabel = [pro.lga, pro.state].filter(Boolean).join(', ') || 'Nigeria';
                const skillsList =
                  pro.skills && pro.skills.length > 0
                    ? pro.skills.slice(0, 3)
                    : pro.specialties && pro.specialties.length > 0
                    ? pro.specialties.slice(0, 3)
                    : [];

                return (
                  <article
                    key={pro.id}
                    className="bg-white rounded-2xl border border-slate-200 p-5 flex flex-col justify-between gap-5 hover:border-slate-300 transition-colors"
                  >
                    <div className="space-y-4">
                      {/* Header: Photo, Name, Verification, Trade, Location */}
                      <div className="flex items-start gap-4">
                        <div
                          onClick={() => navigateTo('pro_profile', { proId: pro.id })}
                          className="relative shrink-0 cursor-pointer"
                        >
                          {showPhoto ? (
                            <img
                              src={photoUrl}
                              alt={pro.fullName}
                              referrerPolicy="no-referrer"
                              onError={() =>
                                setFailedImageIds((prev) => ({ ...prev, [pro.id]: true }))
                              }
                              className="w-16 h-16 rounded-xl object-cover border border-slate-200 shrink-0"
                            />
                          ) : (
                            <div className="w-16 h-16 rounded-xl bg-emerald-50 border border-emerald-200 text-emerald-800 font-bold text-base flex items-center justify-center shrink-0">
                              {pro.fullName
                                .split(' ')
                                .map((n) => n[0])
                                .join('')
                                .slice(0, 2)
                                .toUpperCase()}
                            </div>
                          )}
                        </div>

                        <div className="min-w-0 flex-1 space-y-1">
                          <div className="flex items-center gap-1.5 flex-wrap">
                            <h3
                              onClick={() => navigateTo('pro_profile', { proId: pro.id })}
                              className="font-bold text-base text-slate-900 truncate hover:text-emerald-700 cursor-pointer transition-colors"
                            >
                              {pro.fullName}
                            </h3>
                            {isVerified && (
                              <VerificationBadge profile={pro} status="verified" size="xs" />
                            )}
                          </div>

                          <p className="text-sm font-medium text-emerald-800 truncate">
                            {tradeLabel}
                          </p>

                          <div className="flex items-center gap-1.5 text-xs text-slate-500 flex-wrap">
                            <span className="inline-flex items-center gap-1">
                              <MapPin className="w-3.5 h-3.5 text-slate-400 shrink-0" />
                              <span>{locationLabel}</span>
                            </span>
                            {typeof pro.distanceKm === 'number' && (
                              <>
                                <span aria-hidden="true">·</span>
                                <span className="font-medium text-slate-700 tabular-nums">
                                  {pro.distanceKm.toFixed(1)} km away
                                </span>
                              </>
                            )}
                          </div>
                        </div>
                      </div>

                      {/* Experience, Rating & Skills Summary */}
                      <div className="pt-3 border-t border-slate-100 space-y-2 text-xs text-slate-600">
                        <div className="flex items-center gap-2 flex-wrap">
                          {typeof pro.yearsExperience === 'number' && pro.yearsExperience > 0 && (
                            <span className="tabular-nums">{pro.yearsExperience} yrs experience</span>
                          )}
                          {typeof pro.yearsExperience === 'number' &&
                            pro.yearsExperience > 0 &&
                            hasRealReviews && <span aria-hidden="true">·</span>}
                          {hasRealReviews && (
                            <span className="inline-flex items-center gap-1 font-medium text-slate-800 tabular-nums">
                              <Star className="w-3.5 h-3.5 text-amber-500 fill-amber-500" />
                              {Number(pro.rating).toFixed(1)} ({pro.reviewCount})
                            </span>
                          )}
                          {pro.isAvailable === false && (
                            <>
                              <span aria-hidden="true">·</span>
                              <span className="text-slate-500">Currently unavailable</span>
                            </>
                          )}
                        </div>

                        {skillsList.length > 0 && (
                          <p className="text-xs text-slate-500 truncate">
                            {skillsList.join(' · ')}
                          </p>
                        )}
                      </div>
                    </div>

                    {/* Clear View Profile Action */}
                    <div className="pt-3 border-t border-slate-100 flex items-center justify-between gap-3">
                      <div className="text-xs text-slate-500">
                        {pro.hourlyRate && pro.hourlyRate > 0 ? (
                          <span>
                            From{' '}
                            <strong className="text-slate-900 font-semibold tabular-nums">
                              ₦{pro.hourlyRate.toLocaleString()}
                            </strong>
                          </span>
                        ) : (
                          <span>Quote on request</span>
                        )}
                      </div>

                      <button
                        type="button"
                        onClick={() => navigateTo('pro_profile', { proId: pro.id })}
                        className="px-4 py-2 rounded-xl bg-slate-900 hover:bg-emerald-700 text-white text-xs font-semibold transition-colors cursor-pointer whitespace-nowrap shrink-0"
                      >
                        View Profile
                      </button>
                    </div>
                  </article>
                );
              })}
            </div>
          )}
        </section>
      </div>
    </div>
  );

  if (mapsApiKey) {
    return (
      <APIProvider apiKey={mapsApiKey} region="NG" language="en">
        {pageContent}
      </APIProvider>
    );
  }

  return pageContent;
};
